import { ChevronRight, FolderOpen, Trash2 } from 'lucide-react';
import useConfirmationDialog from '@/hooks/useConfirmationModal';
import { cn } from '@/lib/utils';
import { useCourseLessonsStore } from '@/stores/lessonStore';

import LessonPlanEntry from './LessonPlanEntry';

interface LessonPlanGroupProps {
	group: LessonSection;
}

const LessonPlanGroup = ({ group }: LessonPlanGroupProps) => {
	const { removeGroup, setActiveGroup, activeGroupId } = useCourseLessonsStore();
	const isActive = activeGroupId === group.id;

	const confirm = useConfirmationDialog({
		message: 'Are you sure you want to delete this lesson group and all entries?',
		onConfirm: () => removeGroup(group.id),
		onCancel: () => null,
	});

	return (
		<div className="mb-0.5">
			{confirm.Dialog()}
			{/* Group header */}
			<div
				className={cn(
					'group/row flex items-center gap-1 rounded-md px-2 py-1.5 transition-colors',
					isActive
						? 'bg-primary/10 text-primary'
						: 'text-foreground hover:bg-accent hover:text-foreground'
				)}
			>
				<button
					type="button"
					onClick={() => setActiveGroup(group.id)}
					className="flex flex-1 items-center gap-1.5 min-w-0 text-left"
				>
					<ChevronRight
						size={13}
						className={cn('shrink-0 transition-transform duration-150', isActive && 'rotate-90')}
					/>
					<FolderOpen size={18} className="shrink-0 opacity-70" />
					<span className="truncate text-sm font-medium leading-none">
						{group.title || 'Untitled'}
					</span>
				</button>
				<button
					type="button"
					aria-label="Delete group"
					onClick={() => confirm.onOpen(group.id)}
					className="shrink-0 opacity-0 group-hover/row:opacity-100 flex items-center justify-center h-5 w-5 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all"
				>
					<Trash2 size={18} />
				</button>
			</div>

			{/* Lesson entries — shown only when this group is active */}
			{isActive && (
				<div className="ml-5 mt-0.5 flex flex-col gap-0.5">
					{group.lessons.map((lesson) => (
						<LessonPlanEntry
							key={lesson.id}
							groupId={group.id}
							lesson={lesson}
							isActive={isActive}
						/>
					))}
				</div>
			)}
		</div>
	);
};

export default LessonPlanGroup;
